import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, ColorPicker, Space, Typography, Select } from 'antd';
import { useIntl } from 'react-intl';
import { useUpdateBrandMutation } from '../../services/nestjs-base-api';
import { IBrand } from '../../services/nestjs-base-api/types';
import { useAuth } from '../../hooks/useAuth';
import { TemplateTypeEnum, TemplateTypeOptions } from '../../enums/TemplateTypeEnum';

const { Text } = Typography;

interface EditBrandModalProps {
    visible: boolean;
    brand: IBrand | null;
    onClose: () => void;
    onSuccess?: () => void;
}

interface EditBrandFormData {
    name: string;
    logoUrl?: string;
    primaryColor?: string;
    templateType?: TemplateTypeEnum | null;
}

const EditBrandModal: React.FC<EditBrandModalProps> = ({ 
    visible, 
    brand, 
    onClose, 
    onSuccess 
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [updateBrand, { isLoading }] = useUpdateBrandMutation();
    const [primaryColor, setPrimaryColor] = useState<string>('#4338ca');

    useEffect(() => {
        if (brand && visible) {
            form.setFieldsValue({
                name: brand.name,
                logoUrl: brand.logoUrl || '',
                primaryColor: brand.primaryColor || '#4338ca',
                templateType: brand.templateType || null
            });
            setPrimaryColor(brand.primaryColor || '#4338ca');
        }
    }, [brand, visible, form]);

    const handleSubmit = async (values: EditBrandFormData) => {
        if (!brand || !user?.jwtToken) return;

        try {
            const payload = {
                name: values.name,
                logoUrl: values.logoUrl || undefined,
                primaryColor: primaryColor,
                templateType: values.templateType || undefined,
            };

            await updateBrand({
                jwtToken: user.jwtToken,
                organisationId: user.organisation?.id || 0,
                brandId: brand.id,
                payload
            }).unwrap();

            message.success(
                intl.formatMessage(
                    { id: 'brands.edit.success' },
                    { name: values.name }
                )
            );

            form.resetFields();
            onClose();
            onSuccess?.();
        } catch (error) {
            console.error('Failed to update brand:', error);
            message.error(
                intl.formatMessage({ id: 'brands.edit.error' })
            );
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            title={intl.formatMessage({ id: 'brands.edit.title' })}
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={600}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                style={{ marginTop: 24 }}
            >
                <Form.Item
                    name="name"
                    label={intl.formatMessage({ id: 'brands.form.name' })}
                    rules={[
                        {
                            required: true,
                            message: intl.formatMessage({ id: 'brands.form.nameRequired' })
                        },
                        {
                            min: 2,
                            message: intl.formatMessage({ id: 'brands.form.nameMinLength' })
                        }
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: 'brands.form.namePlaceholder' })}
                        size="large"
                    />
                </Form.Item>

                <Form.Item
                    name="logoUrl"
                    label={intl.formatMessage({ id: 'brands.form.logoUrl' })}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: 'brands.form.logoUrlPlaceholder' })}
                        size="large"
                    />
                </Form.Item>

                <Form.Item
                    label={intl.formatMessage({ id: 'brands.form.primaryColor' })}
                    style={{ marginBottom: 32 }}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <ColorPicker
                            value={primaryColor}
                            onChange={(color) => setPrimaryColor(color.toHexString())}
                            showText
                            size="large"
                            style={{ width: '100%' }}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            This color will be used as the primary brand color throughout the platform
                        </Text>
                    </Space>
                </Form.Item>

                <Form.Item
                    name="templateType"
                    label="Product Type"
                    style={{ marginBottom: 32 }}
                >
                    <Select
                        placeholder="What type of products do you sell?"
                        size="large"
                        allowClear
                        options={TemplateTypeOptions}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        This determines which question set will be used for surveys. You can change this later.
                    </Text>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={handleCancel} size="large">
                            {intl.formatMessage({ id: 'common.cancelButton' })}
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={isLoading}
                            size="large"
                        >
                            {intl.formatMessage({ id: 'brands.edit.submit' })}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EditBrandModal;